import { queryClient } from '@/lib/query/query-client';
import { contextId } from '@/features/inbox/conversation-context-keys';
import { chatInstant, sortMessagesAsc } from '@/features/team-chat/message-order';
import type { AiSuggestion } from './ai-suggestion-store';
type Source = Pick<AiSuggestion, 'conversationId' | 'sourceMessageId' | 'sourceMessageCreatedAt' | 'contextMessageIds'>;

type Interaction = { id?: string; conversation_id?: string; created_at?: string; direction?: string; is_deleted?: boolean };

/** Verify a native source against the loaded persisted pending turn. A cold
 * cache or optimistic outbound cannot authorize showing a proposal. */
export function currentAshleySource(suggestion: Source): boolean {
  const rows = queryClient.getQueryData<Interaction[]>(['messages', suggestion.conversationId]);
  if (!Array.isArray(rows) || !suggestion.sourceMessageId || !suggestion.contextMessageIds || chatInstant(suggestion.sourceMessageCreatedAt) === undefined) return false;
  const visible = rows.filter(row => !row.is_deleted && row.conversation_id === suggestion.conversationId);
  if (visible.some(row => !contextId(row.id) || chatInstant(row.created_at) === undefined)) return false;
  const ordered = sortMessagesAsc(visible);
  const source = ordered.find(row => row.id?.toLowerCase() === suggestion.sourceMessageId!.toLowerCase());
  const sourceAt = chatInstant(suggestion.sourceMessageCreatedAt)!;
  if (!source || source.direction !== 'inbound' || chatInstant(source.created_at) !== sourceAt) return false;
  if (ordered.some(row => row.direction === 'outbound' && chatInstant(row.created_at)! >= sourceAt)) return false;
  const contextIds = new Set(suggestion.contextMessageIds.map(id => id.toLowerCase()));
  let lastReply = -1;
  for (let i = 0; i < ordered.length; i++) if (ordered[i].direction === 'outbound') lastReply = i;
  // Also catches a late-inserted inbound with the same timestamp and a lower
  // UUID. Comparing only the maximum tuple would miss that changed context.
  return ordered.slice(Math.max(lastReply + 1, ordered.length - 30)).every(row => !!row.id && contextIds.has(row.id.toLowerCase()));
}

export function newerAshleyGeneration(next: AiSuggestion, previous?: AiSuggestion): boolean {
  if (!previous?.sourceMessageId || !next.sourceMessageId) return true;
  const before = chatInstant(previous.sourceMessageCreatedAt), after = chatInstant(next.sourceMessageCreatedAt);
  if (before === undefined || after === undefined) return false;
  if (after !== before) return after > before;
  const beforeId = previous.sourceMessageId.toLowerCase(), afterId = next.sourceMessageId.toLowerCase();
  // Equal timestamps do not order host input arrivals by random UUID.
  if (beforeId !== afterId) return true;
  const generatedBefore = chatInstant(previous.createdAt), generatedAfter = chatInstant(next.createdAt);
  return generatedBefore !== undefined && generatedAfter !== undefined && generatedAfter > generatedBefore;
}
